import { useEffect } from 'react';
import type { LatLng, Map as LeafletMap, Point } from 'leaflet';
import { useMap } from 'react-leaflet';

const WHEEL_ZOOM_RATE = 0.006;
const PINCH_ZOOM_RATE = 0.02;
const MOUSE_NOTCH_ZOOM_STEP = 1;
const MAC_MOUSE_NOTCH_DELTA = 4.000244140625;
const MIN_PIXEL_NOTCH_DELTA = 50;
const EASING = 0.3;
const GESTURE_END_DELAY = 200;

function isMouseWheelNotch(event: WheelEvent): boolean {
  if (event.ctrlKey) return false;
  if (event.deltaMode !== WheelEvent.DOM_DELTA_PIXEL) return true;

  const verticalDelta = Math.abs(event.deltaY);
  if (verticalDelta === 0 || event.deltaX !== 0) return false;
  const isMacMouseNotch = verticalDelta % MAC_MOUSE_NOTCH_DELTA === 0;
  const isWindowsMouseNotch =
    Number.isInteger(verticalDelta) && verticalDelta >= MIN_PIXEL_NOTCH_DELTA;
  return isMacMouseNotch || isWindowsMouseNotch;
}

function getWheelZoomChange(event: WheelEvent): number {
  if (isMouseWheelNotch(event)) return -Math.sign(event.deltaY) * MOUSE_NOTCH_ZOOM_STEP;
  const rate = event.ctrlKey ? PINCH_ZOOM_RATE : WHEEL_ZOOM_RATE;
  return -event.deltaY * rate;
}

type InternalMap = LeafletMap & {
  _stop: () => void;
  _moveStart: (zoomChanged: boolean, noMoveStart: boolean) => LeafletMap;
  _move: (center: LatLng, zoom: number) => LeafletMap;
  _moveEnd: (zoomChanged: boolean) => LeafletMap;
};

export function SmoothWheelZoom() {
  const map = useMap() as InternalMap;

  useEffect(() => {
    map.scrollWheelZoom.disable();
    const container = map.getContainer();

    let isZooming = false;
    let hasMoved = false;
    let goalZoom = 0;
    let anchorPoint: Point | null = null;
    let anchorLatLng: LatLng | null = null;
    let frameId = 0;
    let endTimer = 0;

    function step() {
      if (!anchorPoint || !anchorLatLng) return;

      const currentZoom = map.getZoom();
      const nextZoom = Math.round((currentZoom + (goalZoom - currentZoom) * EASING) * 1000) / 1000;

      if (nextZoom !== currentZoom) {
        const offset = anchorPoint.subtract(map.getSize().divideBy(2));
        const center = map.unproject(
          map.project(anchorLatLng, nextZoom).subtract(offset),
          nextZoom,
        );
        if (!hasMoved) {
          map._moveStart(true, false);
          hasMoved = true;
        }
        map._move(center, nextZoom);
      }

      frameId = requestAnimationFrame(step);
    }

    function finish() {
      isZooming = false;
      cancelAnimationFrame(frameId);
      if (hasMoved) map._moveEnd(true);
      hasMoved = false;
    }

    function onWheel(event: WheelEvent) {
      event.preventDefault();

      anchorPoint = map.mouseEventToContainerPoint(event);
      anchorLatLng = map.containerPointToLatLng(anchorPoint);

      if (!isZooming) {
        isZooming = true;
        map._stop();
        goalZoom = map.getZoom();
        frameId = requestAnimationFrame(step);
      }

      goalZoom = Math.min(
        map.getMaxZoom(),
        Math.max(map.getMinZoom(), goalZoom + getWheelZoomChange(event)),
      );

      window.clearTimeout(endTimer);
      endTimer = window.setTimeout(finish, GESTURE_END_DELAY);
    }

    container.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', onWheel);
      window.clearTimeout(endTimer);
      cancelAnimationFrame(frameId);
      map.scrollWheelZoom.enable();
    };
  }, [map]);

  return null;
}
